import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';

/** Left-column admin tools on the business page (wireframe admin view):
 *  Profile Completion, Quick Actions, Page Health. Draws only — navigation
 *  and actions are handled by the host (BusinessComponent). */
@Component({
  selector: 'app-business-admin-cards',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './business-admin-cards.component.html',
  styleUrls: ['./business-admin-cards.component.scss']
})
export class BusinessAdminCardsComponent implements OnChanges {
  /** `business/details` → businessModelList[0] */
  @Input() business: any;

  /** From `business/businessprofile/complete/{id}` (same source as the right
   *  widget); `completePercene` in business/details is not kept up to date. */
  completePercentage: number | null = null;
  private percentRequest = 0;

  /** From `business/services/{id}`: any IT / industry / technology service. */
  hasServices = false;
  private servicesRequest = 0;

  constructor(private api: ApiService) { }

  // Re-fetch on every business reload (the banner reloads after profile
  // edits with the same id); only the latest response is applied.
  ngOnChanges(changes: SimpleChanges): void {
    const id = this.business?.businessId;
    if (changes.business && id) {
      const request = ++this.percentRequest;
      this.api.query('business/businessprofile/complete/' + id).subscribe(res => {
        if (request !== this.percentRequest) { return; }
        this.completePercentage = Number(res?.completePercentage) || 0;
      });

      // Services aren't part of business/details; same source as About → Services.
      const servicesReq = ++this.servicesRequest;
      this.api.query('business/services/' + id).subscribe(res => {
        if (servicesReq !== this.servicesRequest) { return; }
        const d = res?.data;
        this.hasServices = !!(d?.itServices?.length || d?.industriesServices?.length || d?.technologiesServices?.length);
      });
    }
  }

  @Output() editProfile = new EventEmitter<void>();
  /** Page Health "Add": open that About → Profile edit modal. */
  @Output() openEditor = new EventEmitter<'about' | 'details' | 'social'>();
  @Output() postJob = new EventEmitter<void>();
  /** Quick Actions → Manage Employees: the Employees ▾ → Manage Employees page. */
  @Output() manageEmployees = new EventEmitter<void>();
  @Output() analytics = new EventEmitter<void>();
  @Output() adminCenter = new EventEmitter<void>();
  @Output() uploadCover = new EventEmitter<void>();
  @Output() addServices = new EventEmitter<void>();

  get percent(): number {
    const p = this.completePercentage ?? (Number(this.business?.completePercene) || 0);
    return Math.max(0, Math.min(100, Math.round(p)));
  }

  /** Only once the % call has returned, so the button doesn't flash away. */
  get isComplete(): boolean {
    return this.completePercentage !== null && this.percent >= 100;
  }

  // The editor saves an empty description as "<p><br></p>".
  get hasDescription(): boolean {
    const text = (this.business?.aboutCompany || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return !!text;
  }

  get hasLinkedin(): boolean {
    return !!this.business?.userSocialPresence?.linkedin;
  }

  // No cover saves the literal string "undefined" in some records.
  get hasCover(): boolean {
    const b = this.business?.businessBanner;
    return !!b && b !== 'undefined' && b !== 'null';
  }

  get hasStateOfIncorporation(): boolean {
    return !!this.business?.stateOfIncorporation;
  }
}
